import React, { ReactElement, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";

import { baremetalLinks } from "constants/staticLinks";

import BaremetalRecapBox from "components/baremetal/manage/BaremetalRecapBox";
import SectionsBreadCrumbs from "components/shared/breadCrumbs/SectionsBreadCrumbs";
import LoadingComponent from "components/shared/loaders/LoadingComponent";
import MobileSectionMenu from "components/shared/sideMenu/MobileSectionMenu";
import SectionMenu from "components/shared/sideMenu/SectionMenu";

import { postBaremetalDetails } from "redux/handlers/baremetalHandle";

import { getBaremetalDetails } from "redux/selectors/baremetalSelector";

import { useAppDispatch } from "hooks/reduxHook";

import BaremetalDetailsPage from "./BaremetalDetailsPage";
import BaremetalFirewallPage from "./BaremetalFirewallPage";

const BaremetalManagePage = (): ReactElement => {
 const dispatch = useAppDispatch();
 const location = useLocation();
 const params = useParams<{ id: string; section: string }>();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("md"));
 const desktopViewPort2 = useMediaQuery(theme.breakpoints.up("lg"));

 const baremetal = useSelector(getBaremetalDetails);

 const [loading, setLoading] = useState<boolean>(true);

 useEffect(() => {
  (async () => {
   if (params.id) {
    if (baremetal?.id !== Number(params.id)) {
     await dispatch(postBaremetalDetails(Number(params.id)));
    }
   }
   setLoading(false);
  })();
 }, [params.id]);

 const RenderBaremetalPages = (): ReactElement => {
  switch (params.section) {
   case "firewall":
    return <BaremetalFirewallPage />;
   case "specification":
    return <BaremetalDetailsPage />;
   default:
    return <></>;
  }
 };

 return loading ? (
  <LoadingComponent section="servers" />
 ) : (
  <Container maxWidth={false} sx={{ mx: 2, width: "auto" }}>
   <Stack mt={2}>
    <SectionsBreadCrumbs
     links={[
      { name: "home", url: "/" },
      { name: "servers", url: "/servers" },
      { name: "baremetal", url: "/servers" },
      { name: params?.section || "", url: location.pathname }
     ]}
    />
   </Stack>
   <Grid container spacing={4} sx={{ mt: 2 }}>
    {desktopViewPort ? (
     <Grid xs={desktopViewPort ? (!desktopViewPort2 ? 5 : 4) : 0}>
      <Stack>
       <BaremetalRecapBox />
       <Stack py={2} spacing={1} mr={4}>
        <Stack alignItems="flex-start" mb={1}>
         <Typography variant="caption" fontWeight="bold" textTransform="uppercase">
          <FormattedMessage id="app.sections" />
         </Typography>
        </Stack>
        <SectionMenu
         menuList={baremetalLinks.map((item) => ({
          name: item.name,
          url: `/servers/baremetal/manage/${params?.id}/${item.url}`,
          icon: item.icon
         }))}
        />
       </Stack>
      </Stack>
     </Grid>
    ) : (
     <MobileSectionMenu
      links={baremetalLinks}
      disabled={[]}
      url={`/servers/baremetal/manage/${params?.id}`}
     />
    )}
    <Grid xs={desktopViewPort ? (!desktopViewPort2 ? 7 : 8) : 12} spacing={2}>
     <Stack spacing={2}>
      {!desktopViewPort && <BaremetalRecapBox />}
      <RenderBaremetalPages />
     </Stack>
    </Grid>
   </Grid>
  </Container>
 );
};

export default BaremetalManagePage;
